'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { paymentApi } from '@/lib/api/payments';
import { progressApi } from '@/lib/api/progress';

interface OwnedItemsContextType {
  ownedModuleIds: Set<string>;
  ownedCourseIds: Set<string>;
  ownedBundleIds: Set<string>;
  // Per-user progressive-unlock overlay (P-04) — modules with at least one
  // COMPLETED session. Lives here rather than a separate context/provider
  // because it is the same shape of thing as ownership: a per-user set fetched
  // once on auth change, read by catalog cards to override the cached, public,
  // user-agnostic module payload.
  completedModuleIds: Set<string>;
  isLoaded: boolean;
  refresh: () => void;
}

const OwnedItemsContext = createContext<OwnedItemsContextType>({
  ownedModuleIds: new Set(),
  ownedCourseIds: new Set(),
  ownedBundleIds: new Set(),
  completedModuleIds: new Set(),
  isLoaded: false,
  refresh: () => {},
});

export function OwnedItemsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [ownedModuleIds, setOwnedModuleIds] = useState<Set<string>>(new Set());
  const [ownedCourseIds, setOwnedCourseIds] = useState<Set<string>>(new Set());
  const [ownedBundleIds, setOwnedBundleIds] = useState<Set<string>>(new Set());
  const [completedModuleIds, setCompletedModuleIds] = useState<Set<string>>(new Set());
  const [isLoaded, setIsLoaded] = useState(false);

  const fetchOwned = useCallback(async () => {
    try {
      const [data, completedIds] = await Promise.all([
        paymentApi.getOwnedItems(),
        progressApi.getCompletedModuleIds(),
      ]);
      setOwnedModuleIds(new Set(data.moduleIds));
      setOwnedCourseIds(new Set(data.courseIds));
      setOwnedBundleIds(new Set(data.bundleIds));
      setCompletedModuleIds(new Set(completedIds));
    } catch {
      // Silent fail — user may not have token yet or API is down
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOwned();
    } else {
      setOwnedModuleIds(new Set());
      setOwnedCourseIds(new Set());
      setOwnedBundleIds(new Set());
      setCompletedModuleIds(new Set());
      setIsLoaded(true);
    }
  }, [isAuthenticated, fetchOwned]);

  const refresh = useCallback(() => {
    if (isAuthenticated) fetchOwned();
  }, [isAuthenticated, fetchOwned]);

  return (
    <OwnedItemsContext.Provider
      value={{ ownedModuleIds, ownedCourseIds, ownedBundleIds, completedModuleIds, isLoaded, refresh }}
    >
      {children}
    </OwnedItemsContext.Provider>
  );
}

export function useOwnedItems() {
  return useContext(OwnedItemsContext);
}
